<template>
  <BlBackdrop
    title="Export as PDF"
    label="Export"
    :submitLoading="submitLoading"
    @backdrop-close="$emit('close-modal')"
    @backdrop-action="onExport"
  >
    <div
      class="px-8 flex flex-col gap-4 overflow-y-auto"
      :style="{ width: '40rem', maxHeight: 'calc(100vh - 16rem)' }"
    >
      <div class="flex flex-col gap-2">
        <BlTextSubtitle1 text="Date range" />
        <div>
          <div
            v-for="item in rangeOptions"
            :key="item.value"
            class="py-2 flex items-center gap-2 cursor-pointer"
            @click="rangeInput = item.value"
          >
            <BlInputRadio
              :checked="rangeInput === item.value"
              id="export-range"
              name="export-range"
            />
            <BlTextBody2 :text="item.label" class="w-30 text-slate-900" />
            <div v-if="item.start_date" class="flex items-center gap-1">
              <div class="w-24 text-right">
                <BlTextBody2 v-if="item.start_date === '*'" text="Early" />
                <BlTextBody2 v-else :text="item.start_date" />
              </div>
              <div v-if="item.end_date">
                <BlTextBody2 text="-" />
              </div>
              <div v-if="item.end_date" class="w-24 text-right">
                <BlTextBody2 :text="item.end_date" />
              </div>
            </div>
          </div>
          <div v-if="rangeInput === 'custom'" class="flex items-center gap-2">
            <BlInputDate
              :input="startDateInput"
              @changed="inputHandler"
              disabledManualInput
            />
            <BlIcon icon="arrow-right" color="slate-6" class="w-6 h-6" />
            <BlInputDate
              :input="endDateInput"
              @changed="inputHandler"
              disabledManualInput
            />
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <BlTextSubtitle1 text="Transaction type" />
        <div>
          <div
            v-for="item in transactionTypeOptions"
            :key="item.value"
            class="py-2 flex items-center gap-2 cursor-pointer"
            @click="onChangeType(item.value)"
          >
            <BlInputCheck
              :checked="transactionTypes.includes(item.value)"
              :id="`export-type-${item.value}`"
              :name="`export-type-${item.value}`"
            />
            <BlTextBody2 :text="item.label" class="text-slate-900" />
          </div>
        </div>
      </div>
    </div>
  </BlBackdrop>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

import BlBackdrop from "../../BlBackdrop.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputDate from "../../BlInputDate.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputToggle from "../../BlInputToggle.vue";
import BlInputRadio from "../../BlInputRadio.vue";
import BlInputCheck from "../../BlInputCheck.vue";

export default {
  name: "ExportCreditTransactionModal",
  components: {
    BlBackdrop,
    BlTextSubtitle1,
    BlTextBody2,
    BlInputDate,
    BlInputText,
    BlInputButton,
    BlIcon,
    BlInputToggle,
    BlInputRadio,
    BlInputCheck
  },
  data() {
    return {
      submitLoading: false,
      rangeInput: "today",
      rangeOptions: [
        {
          label: "Today",
          value: "today",
          start_date: moment().format("DD MMM YYYY")
        },
        {
          label: "Last 7 days",
          value: "last_7_days",
          start_date: moment()
            .subtract(7, "days")
            .format("DD MMM YYYY"),
          end_date: moment().format("DD MMM YYYY")
        },
        {
          label: "Current month",
          value: "current_month",
          start_date: moment()
            .startOf("month")
            .format("DD MMM YYYY"),
          end_date: moment()
            .endOf("month")
            .format("DD MMM YYYY")
        },
        {
          label: "Last month",
          value: "last_month",
          start_date: moment()
            .subtract(1, "months")
            .startOf("month")
            .format("DD MMM YYYY"),
          end_date: moment()
            .subtract(1, "months")
            .endOf("month")
            .format("DD MMM YYYY")
        },
        { label: "All", value: "all" },
        { label: "Custom", value: "custom" }
      ],
      startDateInput: {
        model: moment().format("YYYY-MM-DD"),
        max: ""
      },
      endDateInput: {
        model: moment().format("YYYY-MM-DD"),
        min: ""
      },
      //
      transactionTypes: [
        "credit_addition",
        "manual_subtraction",
        "scheduled_appointment",
        "completed_appointment",
        "cancelled_appointment",
        "rescheduled_appointment"
      ],
      transactionTypeOptions: [
        { value: "credit_addition", label: "Credit addition" },
        { value: "manual_subtraction", label: "Manual subtraction" },
        { value: "scheduled_appointment", label: "Scheduled appointment" },
        { value: "completed_appointment", label: "Completed appointment" },
        { value: "cancelled_appointment", label: "Cancelled appointment" },
        { value: "rescheduled_appointment", label: "Rescheduled appointment" }
      ]
    };
  },
  computed: {
    ...mapGetters(["client"])
  },
  watch: {
    startDateInput: {
      deep: true,
      immediate: true,
      handler(val) {
        const date = val.model;
        if (date) {
          this.endDateInput.min = date;
        } else {
          this.endDateInput.min = "";
        }
      }
    },
    endDateInput: {
      deep: true,
      immediate: true,
      handler(val) {
        const date = val.model;
        if (date) {
          this.startDateInput.max = date;
        } else {
          this.startDateInput.max = "";
        }
      }
    }
  },
  methods: {
    ...mapActions(["exportCreditTransactions"]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    onChangeType(type) {
      if (this.transactionTypes.includes(type)) {
        this.transactionTypes = this.transactionTypes.filter(i => i !== type);
      } else {
        this.transactionTypes.push(type);
      }
    },
    //
    async onExport() {
      let params = `?export=true`;
      if (this.rangeInput === "custom") {
        params += `&start_date=${this.startDateInput.model}&end_date=${this.endDateInput.model}`;
      } else if (!this.rangeInput === "all") {
        params += `&filter_by=${this.rangeInput}`;
      }

      if (this.transactionTypes.length) {
        params += `&transaction_type=${this.transactionTypes.join(",")}`;
      }

      this.submitLoading = true;
      const response = await this.exportCreditTransactions({
        client_id: this.client.id,
        params
      });

      const href = URL.createObjectURL(response.data);
      const link = document.createElement("a");

      let filename = "";
      let disposition = response.headers["content-disposition"];
      let filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      let matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, "");
      }

      link.href = href;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(href);
      this.$emit("close-modal");
      this.submitLoading = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/creditTransaction/ExportCreditTransactionModal.vue